/**
 * Die Zustandsableitung -- das Herz des Systems.
 *
 * Aus der Liste der Buchungen zu einem Artikel ergibt sich sein aktueller
 * Zustand. Nichts anderes bestimmt ihn. Die Statusspalte auf `core.artikel`
 * ist ein Lesecache und muss jederzeit durch einen Aufruf dieser Funktion
 * wiederherstellbar sein (CLAUDE.md §3.2).
 *
 * Alles hier ist eine **reine Funktion**: gleiche Eingabe, gleiche Ausgabe,
 * keine Datenbank, keine Uhr, keine Zufallszahlen. Genau deshalb kann derselbe
 * Code im Browser laufen und dort offline denselben Zustand berechnen wie der
 * Server -- das ist die Voraussetzung dafuer, dass die Oberflaeche das
 * Ergebnis sofort zeigt und nicht erst nach Serverantwort (CLAUDE.md §3.3).
 */
import { istStueckgut, } from "@thw/contracts";
import { statusAusLog } from "./status.js";
/**
 * Reihenfolge zweier Buchungen.
 *
 * Erster Schluessel ist `erfasst_am`: die neuere Buchung gewinnt
 * (KONZEPT.md §15.3). Weil zwei Geraete denselben Zeitpunkt liefern koennen --
 * beim Mehrfachscan sogar regelmaessig --, folgen `geraet` und `id` als
 * weitere Schluessel. Ohne sie waere die Reihenfolge bei Gleichstand dem
 * Zufall der Sortierung ueberlassen, und zwei Clients koennten aus demselben
 * Log verschiedene Zustaende ableiten. Die zusaetzlichen Schluessel sind
 * fachlich willkuerlich, aber auf jedem Geraet gleich -- und darauf kommt es an.
 */
export function vergleicheBuchungen(a, b) {
    // Zeichenketten taugen nicht zum Vergleich: "12:00:00+01:00" und
    // "11:00:00Z" bezeichnen denselben Augenblick, sehen aber verschieden aus.
    const zeitA = Date.parse(a.erfasst_am);
    const zeitB = Date.parse(b.erfasst_am);
    if (zeitA !== zeitB)
        return zeitA - zeitB;
    if (a.geraet !== b.geraet)
        return a.geraet < b.geraet ? -1 : 1;
    if (a.id !== b.id)
        return a.id < b.id ? -1 : 1;
    return 0;
}
/** Buchungen in der Reihenfolge, in der sie gelten. Aendert die Eingabe nicht. */
export function sortiereBuchungen(buchungen) {
    return [...buchungen].sort(vergleicheBuchungen);
}
/** Die abgeleiteten Kennzeichen eines Status. */
function kennzeichen(verbleib, status, regeln) {
    const einsatzbereit = regeln.einsatzbereit.has(status);
    const archiviert = regeln.archivierend.has(status);
    return {
        einsatzbereit,
        archiviert,
        verfuegbar: einsatzbereit && verbleib === "im_lager" && !archiviert,
    };
}
/** Zustand eines Artikels, zu dem es noch keine einzige Buchung gibt. */
export function zustandOhneBuchung(regeln) {
    return {
        verbleib: "nicht_erfasst",
        status: regeln.standard,
        ...kennzeichen("nicht_erfasst", regeln.standard, regeln),
        lagerort_id: null,
        einsatzstelle_id: null,
        bestand: null,
        letzte_buchung_id: null,
        letzte_buchung_am: null,
    };
}
/**
 * Bewegt sich ein archivierter Artikel, ist er offenbar wieder da.
 *
 * KONZEPT.md §7.2: Taucht ein verlorenes Geraet wieder auf, genuegt es, es
 * einzubuchen. Dann gilt wieder der Standardstatus. Ein **defektes** Geraet
 * bleibt dagegen defekt, wenn es bewegt wird -- es in die Werkstatt zu bringen
 * repariert es nicht (§7.4). Den Unterschied macht das Kennzeichen
 * `archiviert` des Status.
 */
function nachBewegung(status, regeln) {
    return regeln.archivierend.has(status) ? regeln.standard : status;
}
/**
 * Wirkung einer einzelnen Buchung auf den Zustand.
 *
 * **Verbleib und Status sind getrennt** (ADR 0005). Bewegungen aendern den
 * Verbleib und den Ort, nie den Status -- bis auf die eine Ausnahme oben.
 * Statusaenderungen aendern den Status, nie den Ort.
 *
 * Die Zuordnung von Buchungsart zu Wirkung ist bewusst hier und nicht in der
 * Buchung: Eine gespeicherte Buchung soll sagen, *was getan wurde*, nicht
 * *was daraus folgen sollte*. Aendert sich die Regel, wird sie hier geaendert,
 * und das Log bleibt gueltig.
 */
function anwenden(lauf, b, stueckgut, regeln) {
    switch (b.art) {
        // Auf einem realen Lagerort eingebucht -- damit gilt der Artikel als
        // zurueck, gleich ob er vorher im Einsatz oder verliehen war
        // (KONZEPT.md §7.1).
        case "einbuchen":
        case "ruecknahme":
            return {
                verbleib: "im_lager",
                status: nachBewegung(lauf.status, regeln),
                lagerort_id: b.ziel_lagerort_id,
                einsatzstelle_id: null,
                bestand: stueckgut ? lauf.bestand : lauf.bestand + (b.menge ?? 0),
            };
        // Ortswechsel im Haus. Am Bestand aendert er nichts -- die Ware bleibt
        // unsere, sie liegt nur woanders. Und am Status auch nicht: Ein defektes
        // Geraet, das in die Werkstatt kommt, bleibt defekt.
        case "umlagerung":
            return {
                ...lauf,
                verbleib: "im_lager",
                status: nachBewegung(lauf.status, regeln),
                lagerort_id: b.ziel_lagerort_id,
                einsatzstelle_id: null,
            };
        // Bei Einsatz, Entnahme und Ausgabe steht ein Ziel-Lagerort nur, wenn der
        // Artikel in einem Packstueck mitgeht (`packstueck.ts`). Er bleibt dann
        // dem Packstueck zugeordnet -- draussen wie drinnen -- und kommt mit ihm
        // zurueck. Sonst ist das Feld leer und der Artikel an keinem Lagerort.
        case "einsatz":
            return {
                verbleib: "im_einsatz",
                status: nachBewegung(lauf.status, regeln),
                lagerort_id: b.ziel_lagerort_id,
                einsatzstelle_id: b.einsatzstelle_id,
                bestand: stueckgut ? lauf.bestand : lauf.bestand - (b.menge ?? 0),
            };
        // Entnahme ohne benanntes Ziel. KONZEPT.md §6.1 laesst das ausdruecklich
        // zu: Material darf auch ohne Einsatzstelle in den Einsatz gebucht werden.
        case "entnehmen":
            return {
                verbleib: "im_einsatz",
                status: nachBewegung(lauf.status, regeln),
                lagerort_id: b.ziel_lagerort_id,
                einsatzstelle_id: null,
                bestand: stueckgut ? lauf.bestand : lauf.bestand - (b.menge ?? 0),
            };
        case "ausgabe":
            return {
                verbleib: "ausgeliehen",
                status: nachBewegung(lauf.status, regeln),
                lagerort_id: b.ziel_lagerort_id,
                einsatzstelle_id: null,
                bestand: stueckgut ? lauf.bestand : lauf.bestand - (b.menge ?? 0),
            };
        // Nur der Status. Der Ort bleibt: Ein defektes Geraet liegt weiterhin
        // dort, wo es liegt (KONZEPT.md §7.2), und ein Schaden an der
        // Einsatzstelle aendert nichts daran, wo das Geraet ist.
        case "statusaenderung":
            return {
                ...lauf,
                status: b.status === null ? lauf.status : statusAusLog(b.status, regeln),
            };
        // Gegenbuchung: setzt, was sie ausdruecklich nennt, und laesst den Rest
        // stehen. Nennt sie einen Lagerort, liegt der Artikel dort. Bei
        // Verbrauchsmaterial traegt sie einen absoluten Bestand -- das Ergebnis
        // einer Zaehlung, das unabhaengig davon richtig ist, ob das Geraet den
        // vorherigen Stand kannte.
        case "korrektur":
            return {
                verbleib: b.ziel_lagerort_id === null ? lauf.verbleib : "im_lager",
                status: b.status === null ? lauf.status : statusAusLog(b.status, regeln),
                lagerort_id: b.ziel_lagerort_id ?? lauf.lagerort_id,
                einsatzstelle_id: b.ziel_lagerort_id === null ? lauf.einsatzstelle_id : null,
                bestand: stueckgut ? lauf.bestand : (b.menge_absolut ?? lauf.bestand),
            };
    }
}
/**
 * Berechnet den aktuellen Zustand eines Artikels aus seinen Buchungen.
 *
 * Buchungen, die zu einem anderen Artikel gehoeren, werden ignoriert -- so
 * kann der Aufrufer bedenkenlos das ganze Log uebergeben.
 *
 * `regeln` kommt aus den eingerichteten Status (`statusRegeln`).
 */
export function zustandBerechnen(artikel, buchungen, regeln) {
    const eigene = sortiereBuchungen(buchungen.filter((b) => b.artikel_id === artikel.id));
    const letzte = eigene.at(-1);
    if (letzte === undefined)
        return zustandOhneBuchung(regeln);
    const stueckgut = istStueckgut(artikel.art);
    let lauf = {
        verbleib: "nicht_erfasst",
        status: regeln.standard,
        lagerort_id: null,
        einsatzstelle_id: null,
        bestand: 0,
    };
    for (const b of eigene)
        lauf = anwenden(lauf, b, stueckgut, regeln);
    return {
        verbleib: lauf.verbleib,
        status: lauf.status,
        // Ein archivierender Status (verloren, gestohlen) nimmt den Artikel aus
        // den aktiven Listen (KONZEPT.md §7.2). Der Status bleibt als Grund stehen.
        ...kennzeichen(lauf.verbleib, lauf.status, regeln),
        lagerort_id: lauf.lagerort_id,
        einsatzstelle_id: lauf.einsatzstelle_id,
        // Ein negativer Bestand wird bewusst nicht abgefangen. Er bedeutet, dass
        // mehr entnommen als eingebucht wurde, und ist damit ein sichtbarer
        // Hinweis auf eine fehlende Zugangsbuchung. Auf null zu klemmen wuerde
        // den Fehler verstecken, und die Inventur muesste ihn spaeter suchen.
        bestand: stueckgut ? null : lauf.bestand,
        letzte_buchung_id: letzte.id,
        letzte_buchung_am: letzte.erfasst_am,
    };
}
/**
 * Der Status, den ein Artikel direkt vor seiner letzten Statusaenderung hatte
 * -- gerechnet mit den Buchungen, die vor `vor` gelten (ADR 0025).
 *
 * Daran erkennt der Server ein „Rueckgaengig“, ohne dass die Buchung sagt,
 * was sie zuruecknimmt: Einen Status mit „Nur Admin“ darf jeder setzen, wenn
 * der Artikel genau ihn vorher hatte. Auch ein Wechsel, den eine Bewegung
 * ausloest (ein verlorenes Teil taucht auf und bekommt den Standard), zaehlt
 * als Statusaenderung. `null`, wenn sich der Status nie geaendert hat.
 */
export function statusVorLetzterAenderung(artikel, buchungen, vor, regeln) {
    const eigene = sortiereBuchungen(buchungen.filter((b) => b.artikel_id === artikel.id && b.id !== vor.id && vergleicheBuchungen(b, vor) < 0));
    const stueckgut = istStueckgut(artikel.art);
    let lauf = {
        verbleib: "nicht_erfasst",
        status: regeln.standard,
        lagerort_id: null,
        einsatzstelle_id: null,
        bestand: 0,
    };
    let vorher = null;
    for (const b of eigene) {
        const danach = anwenden(lauf, b, stueckgut, regeln);
        if (danach.status !== lauf.status)
            vorher = lauf.status;
        lauf = danach;
    }
    return vorher;
}
/**
 * Wurde eine Buchung von einer neueren ueberstimmt?
 *
 * Liefert die Buchung, die stattdessen gilt, oder `null`. Daraus entsteht der
 * Hinweis an das ueberstimmte Geraet (KONZEPT.md §15.4) -- ausdruecklich als
 * Mitteilung und nicht als Dialog: Niemand loest im Einsatz Konflikte auf.
 *
 * Eine Buchung, die nur den Bestand bewegt, ueberstimmt nichts: Bei
 * Verbrauchsmaterial zaehlen alle Buchungen zusammen, statt dass eine gewinnt.
 *
 * Ueberstimmt wird nur, was **dieselbe Seite** betrifft (ADR 0005). Kommt eine
 * aeltere Statusaenderung nach einer neueren Umlagerung an, gilt sie trotzdem:
 * Die Umlagerung hat den Ort geaendert, nicht den Status.
 */
export function ueberstimmtVon(artikel, buchung, alle) {
    if (!istStueckgut(artikel.art))
        return null;
    const eigene = sortiereBuchungen(alle.filter((b) => b.artikel_id === artikel.id));
    const stelle = eigene.findIndex((b) => b.id === buchung.id);
    const danach = stelle === -1 ? eigene : eigene.slice(stelle + 1);
    const seite = seiten(buchung);
    const gewinner = danach.filter((b) => {
        const andere = seiten(b);
        return (seite.ort && andere.ort) || (seite.status && andere.status);
    });
    return gewinner.at(-1) ?? null;
}
/** Was eine Buchung festlegt: den Ort, den Status oder beides. */
function seiten(b) {
    const bewegung = [
        "einbuchen",
        "ruecknahme",
        "umlagerung",
        "einsatz",
        "entnehmen",
        "ausgabe",
    ];
    if (bewegung.includes(b.art))
        return { ort: true, status: false };
    if (b.art === "statusaenderung")
        return { ort: false, status: true };
    // Korrektur: was sie nennt.
    return { ort: b.ziel_lagerort_id !== null, status: b.status !== null };
}
//# sourceMappingURL=zustand.js.map